import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { MARKETPLACE_KEYWORD_MAX_LENGTH } from '../../utils/marketplace/marketplaceKeywordSearch';
import { getTypedMarketplaceSuggestions } from '../../utils/marketplace/marketplaceSearchSuggestions';
import HeaderMarketplaceSearchSuggestions from './HeaderMarketplaceSearchSuggestions';
import HeaderAllCategories from './HeaderAllCategories';
import { HEADER_SEARCH_CONTAINER_NAME } from '../../styles/navigation/HeaderStyles';
import { FONT_SIZES, RADIUS_PILL } from '../../Theme';

/**
 * Chrome search (Header / MobileBrandBar).
 * Focus keeps the pill closed until the query has text.
 * Typed queries expand the input and suggestions into one rounded card.
 * Combobox keyboard: arrows highlight; Enter selects highlight or submits.
 * All Categories sits in the input row before submit and scales with the pill.
 */
function HeaderMarketplaceSearch({
  value,
  onChange,
  onSubmit,
  onClear,
  onSelectSuggestion = () => {},
  onSelectCategory = () => {},
  onSelectPlatform = () => {},
  onSelectContentFormat = () => {},
  onBrowseAllCategories = () => {},
  userType = null,
  placeholder = 'Search',
  inputId = 'header-marketplace-search',
  dense = false,
  hideMarketplaceFacets = false,
}) {
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = `${inputId}-suggestions`;
  const optionIdPrefix = `${inputId}-option`;
  const trimmed = String(value || '').trim();
  const isEmptyQuery = trimmed.length === 0;

  let suggestionItems = [];
  if (open && !hideMarketplaceFacets && !isEmptyQuery) {
    suggestionItems = getTypedMarketplaceSuggestions(trimmed, userType);
  }
  const showAllCategories = !dense && !hideMarketplaceFacets;

  const showSuggestions = open && suggestionItems.length > 0;
  const showOverlay = showSuggestions || categoriesOpen;
  const activeOptionId = activeIndex >= 0 && activeIndex < suggestionItems.length
    ? `${optionIdPrefix}-${activeIndex}`
    : undefined;

  useEffect(() => {
    setActiveIndex(-1);
  }, [trimmed, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const closePanel = () => {
    setOpen(false);
    setCategoriesOpen(false);
    setActiveIndex(-1);
  };

  const handleCategoriesOpenChange = (isOpen) => {
    setCategoriesOpen(isOpen);
    if (isOpen) {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const commitTypedSubmit = () => {
    const result = onSubmit();
    if (result && result.navigated === false) {
      setOpen(true);
      setActiveIndex(-1);
      return;
    }
    closePanel();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Pink submit always commits the typed query — never the highlighted row.
    commitTypedSubmit();
  };

  const handleClear = () => {
    onClear();
    setOpen(true);
    setActiveIndex(-1);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSelectSuggestion = (item) => {
    closePanel();
    onSelectSuggestion(item);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      return;
    }

    if (event.key === 'Tab') {
      closePanel();
      return;
    }

    if (event.key === 'Enter') {
      if (activeIndex >= 0 && suggestionItems[activeIndex]) {
        event.preventDefault();
        closePanel();
        onSelectSuggestion(suggestionItems[activeIndex]);
      }
      // No highlight → allow form submit (typed query / browse).
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(-1);
        return;
      }
      if (!suggestionItems.length) return;
      setActiveIndex((prev) => {
        if (prev < 0) return 0;
        return (prev + 1) % suggestionItems.length;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(-1);
        return;
      }
      if (!suggestionItems.length) return;
      setActiveIndex((prev) => {
        if (prev < 0) return suggestionItems.length - 1;
        return (prev - 1 + suggestionItems.length) % suggestionItems.length;
      });
    }
  };

  const pillHeight = 44;
  const collapsedRadius = `${RADIUS_PILL}px`;
  const cardRadius = (theme) => theme.spacing(2.5);

  return (
    <Box
      ref={rootRef}
      component="form"
      role="search"
      onSubmit={handleSubmit}
      data-dense={dense ? 'true' : 'false'}
      data-search-container={HEADER_SEARCH_CONTAINER_NAME}
      sx={{
        width: '100%',
        minWidth: 0,
        position: 'relative',
        containerType: 'inline-size',
        containerName: HEADER_SEARCH_CONTAINER_NAME,
      }}
    >
      {showOverlay ? (
        <Box
          aria-hidden
          sx={{ height: pillHeight }}
        />
      ) : null}
      <Box
        ref={cardRef}
        sx={{
          position: showOverlay ? 'absolute' : 'relative',
          top: showOverlay ? (theme) => `-${theme.spacing(1.25)}` : 0,
          left: showOverlay ? (theme) => `-${theme.spacing(1.25)}` : 0,
          right: showOverlay ? (theme) => `-${theme.spacing(1.25)}` : 0,
          zIndex: showOverlay ? 10020 : 2,
          display: 'flex',
          flexDirection: 'column',
          height: showOverlay ? 'auto' : pillHeight,
          boxSizing: 'border-box',
          p: showOverlay ? 1.25 : 0,
          pb: showOverlay ? 1 : 0,
          borderRadius: showOverlay ? cardRadius : 0,
          bgcolor: showOverlay ? 'background.paper' : 'transparent',
          border: showOverlay ? 1 : 0,
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Box
          data-testid="header-marketplace-search-pill"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            height: pillHeight,
            flexShrink: 0,
            pl: 2,
            pr: 0.5,
            minWidth: 0,
            boxSizing: 'border-box',
            borderRadius: collapsedRadius,
            bgcolor: 'background.soft',
            boxShadow: 'none',
            [`@container ${HEADER_SEARCH_CONTAINER_NAME} (max-width: 560px)`]: {
              gap: 0.25,
              pl: 1.5,
            },
          }}
        >
          <InputBase
            inputRef={inputRef}
            id={inputId}
            value={value}
            onChange={(event) => {
              onChange(event.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => {
              setOpen(true);
              setActiveIndex(-1);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            type="search"
            inputProps={{
              role: 'combobox',
              autoComplete: 'off',
              inputMode: 'search',
              maxLength: MARKETPLACE_KEYWORD_MAX_LENGTH,
              'aria-label': placeholder,
              'aria-autocomplete': hideMarketplaceFacets ? 'none' : 'list',
              'aria-controls': showSuggestions ? listId : undefined,
              'aria-expanded': showOverlay,
              'aria-activedescendant': activeOptionId,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: dense ? FONT_SIZES.bodySm : FONT_SIZES.body,
              [`@container ${HEADER_SEARCH_CONTAINER_NAME} (max-width: 560px)`]: {
                fontSize: FONT_SIZES.bodySm,
              },
              '& input': {
                py: 0.5,
                pr: 0.5,
                border: 'none',
                outline: 'none',
                caretColor: (theme) => theme.palette.text.primary,
                '&::-webkit-search-cancel-button': {
                  display: 'none',
                },
                '&::placeholder': {
                  opacity: 1,
                  color: 'text.secondary',
                },
              },
            }}
          />
          {value ? (
            <IconButton
              type="button"
              size="small"
              onClick={handleClear}
              aria-label="Clear search"
              sx={{
                flexShrink: 0,
                width: 36,
                height: 36,
                color: 'text.secondary',
                bgcolor: 'background.soft',
                '&:hover': {
                  bgcolor: 'background.softHover',
                  color: 'text.primary',
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : null}
          {showAllCategories ? (
            <>
              <Box
                aria-hidden
                data-testid="header-all-categories-divider"
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  my: 1,
                  bgcolor: 'divider',
                  flexShrink: 0,
                }}
              />
              <HeaderAllCategories
                userType={userType}
                menuAnchorRef={cardRef}
                closeWhen={open}
                onSelectCategory={onSelectCategory}
                onSelectPlatform={onSelectPlatform}
                onSelectContentFormat={onSelectContentFormat}
                onBrowseAll={onBrowseAllCategories}
                onOpenChange={handleCategoriesOpenChange}
              />
            </>
          ) : null}
          <IconButton
            type="submit"
            aria-label="Search"
            data-testid="header-marketplace-search-submit"
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              bgcolor: 'favorite.main',
              color: 'common.white',
              '&:hover': {
                bgcolor: 'favorite.dark',
              },
              '&:active': {
                bgcolor: 'favorite.dark',
              },
              '&.Mui-focusVisible': {
                outline: (theme) => `2px solid ${theme.palette.favorite.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: dense ? 18 : 20 }} />
          </IconButton>
        </Box>
        {showSuggestions ? (
          <HeaderMarketplaceSearchSuggestions
            items={suggestionItems}
            query={trimmed}
            onSelect={handleSelectSuggestion}
            listId={listId}
            activeIndex={activeIndex}
            optionIdPrefix={optionIdPrefix}
          />
        ) : null}
      </Box>
    </Box>
  );
}

export default HeaderMarketplaceSearch;
